import NextImage from 'next/image';
import { cn } from 'lib/utils';
import { type ImageField, type LinkField } from 'lib/types';
import {
  type BloomProps,
  Bloom,
  type ButtonProps,
  Button,
  Icon,
  Link,
  RichText,
  Wrapper,
} from 'ui';

export type TextImageProps = {
  className?: string;
  title: string;
  content?: string;
  image: ImageField;
  alignment?: 'left' | 'right';
  textLink?: LinkField;
  buttonLink?: LinkField;
  buttonLinkVariant?: ButtonProps['variant'];
  bloom?: BloomProps;
};

const TextImage = ({
  className,
  alignment = 'left',
  title,
  content,
  textLink,
  buttonLink,
  buttonLinkVariant = 'primary',
  image,
  bloom,
}: TextImageProps) => {
  return (
    <Wrapper
      as="section"
      variant="page-full"
      className={cn(
        'relative flex flex-col-reverse gap-12 lg:gap-24 xl:gap-32 lg:items-center',
        {
          'lg:flex-row': alignment === 'left',
          'lg:flex-row-reverse': alignment === 'right',
        },
        className
      )}
    >
      <Bloom {...bloom} />

      {/* Content */}
      <div className="lg:grow lg:shrink lg:basis-1/2 flex flex-col gap-6 relative z-2">
        <header className="flex flex-col gap-4 lg:gap-6">
          <h2 className="text-h3">{title}</h2>
          {!!content && <RichText theme="neutral" {...{ content }} />}
        </header>

        {!!textLink && (
          <div>
            <Link
              href={textLink.href}
              target={textLink.target}
              variant="neutral"
            >
              <span>{textLink.text}</span>
              <Icon
                name="carousel-arrow-right"
                className="inline-block align-middle ml-1.5 w-4 h-auto"
              />
            </Link>
          </div>
        )}

        {!!buttonLink && (
          <div>
            <Button
              href={buttonLink.href}
              target={buttonLink.target}
              variant={buttonLinkVariant}
            >
              {buttonLink.text}
            </Button>
          </div>
        )}
      </div>

      {/* Image */}
      <div className="lg:grow lg:shrink lg:basis-1/2 relative z-2">
        <NextImage
          src={image.src}
          alt={image.alt}
          width={image.width}
          height={image.height}
          className="w-full h-auto object-contain rounded-lg"
        />
      </div>
    </Wrapper>
  );
};

export default TextImage;
